import React, { useMemo } from 'react';
import {
  MapContainer,
  TileLayer,
  Polyline,
  CircleMarker,
  Tooltip,
  Marker,
  useMap,
} from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import MapLibreGLLayer from '../yard-layout/MapLibreGLLayer';

const OSM_TILE = 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png';
const OSM_ATTRIBUTION = '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors';
const MAX_DISPLAY_POINTS = 2000; // bounded ceiling; spatial dedup below removes most redundancy first
const MIN_MOVE_METERS = 30; // collapse jitter/dwell clusters: keep a point only once the device moved this far

/**
 * Haversine distance in meters between two [lat, lng]-ish points.
 * Mirrors getDistanceFromLatLonInMeters in src/services/yard.service.cjs (backend, not importable here).
 */
function haversineMeters(lat1, lng1, lat2, lng2) {
  const toRad = (deg) => (deg * Math.PI) / 180;
  const R = 6371000; // Earth radius in meters
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(a)));
}

/**
 * Perpendicular distance (meters) from point p to the segment a–b, using a local
 * equirectangular projection around `a` (accurate at city scale).
 */
function perpendicularMeters(p, a, b) {
  const cosLat = Math.cos((a.latitude * Math.PI) / 180);
  const toXY = (q) => ({
    x: (q.longitude - a.longitude) * cosLat * 111320,
    y: (q.latitude - a.latitude) * 110540,
  });
  const P = toXY(p);
  const B = toXY(b);
  const segLenSq = B.x * B.x + B.y * B.y;
  if (segLenSq === 0) return Math.hypot(P.x, P.y);
  let t = (P.x * B.x + P.y * B.y) / segLenSq;
  t = Math.max(0, Math.min(1, t));
  return Math.hypot(P.x - t * B.x, P.y - t * B.y);
}

/**
 * Ramer–Douglas–Peucker polyline simplification (epsilon in meters). Keeps corners/detours.
 */
function rdp(points, epsilonMeters) {
  if (points.length <= 2) return points;
  let maxDist = 0;
  let index = 0;
  const end = points.length - 1;
  for (let i = 1; i < end; i++) {
    const d = perpendicularMeters(points[i], points[0], points[end]);
    if (d > maxDist) {
      maxDist = d;
      index = i;
    }
  }
  if (maxDist > epsilonMeters) {
    const left = rdp(points.slice(0, index + 1), epsilonMeters);
    const right = rdp(points.slice(index), epsilonMeters);
    return left.slice(0, -1).concat(right);
  }
  return [points[0], points[end]];
}

/**
 * Reduce a device path for display while preserving every distinct location (range-independent).
 * Stage A: drop points within MIN_MOVE_METERS of the last kept point (collapses dwell/jitter clusters).
 * Stage B: if still over maxPoints, simplify the line with RDP (escalating epsilon) — never index decimation.
 */
function simplifyPath(points, maxPoints = MAX_DISPLAY_POINTS) {
  if (!Array.isArray(points) || points.length <= 2) return points || [];

  // Stage A — distance dedup
  const deduped = [points[0]];
  let last = points[0];
  for (let i = 1; i < points.length - 1; i++) {
    const p = points[i];
    if (haversineMeters(last.latitude, last.longitude, p.latitude, p.longitude) >= MIN_MOVE_METERS) {
      deduped.push(p);
      last = p;
    }
  }
  deduped.push(points[points.length - 1]);

  if (deduped.length <= maxPoints) return deduped;

  // Stage B — RDP fallback, escalate epsilon until under the ceiling
  let epsilon = MIN_MOVE_METERS;
  let simplified = deduped;
  for (let iter = 0; iter < 12 && simplified.length > maxPoints; iter++) {
    simplified = rdp(deduped, epsilon);
    epsilon *= 2;
  }
  return simplified;
}

/**
 * Fit map bounds to show all path points. Safe when empty or single point.
 */
function FitBounds({ paths }) {
  const map = useMap();
  const allPoints = useMemo(() => {
    const flat = [];
    (paths || []).forEach(({ points }) => {
      (points || []).forEach((p) => {
        if (p != null && p.latitude != null && p.longitude != null)
          flat.push([Number(p.latitude), Number(p.longitude)]);
      });
    });
    return flat;
  }, [paths]);

  React.useEffect(() => {
    if (allPoints.length === 0) return;
    try {
      const bounds = L.latLngBounds(allPoints);
      map.fitBounds(bounds, { padding: [40, 40], maxZoom: 16 });
    } catch (_) {}
  }, [map, allPoints]);

  return null;
}

/**
 * Ripple/pulse marker for the newest (last) point of a path.
 */
function NewestPointMarker({ position, color, deviceName, timestamp }) {
  const icon = L.divIcon({
    className: 'gps-travel-newest-marker',
    html: `
      <div class="gps-travel-ripple-wrapper" style="--ripple-color: ${color}">
        <span class="gps-travel-ripple-ring"></span>
        <span class="gps-travel-ripple-ring gps-travel-ripple-delay"></span>
        <span class="gps-travel-ripple-dot" style="background-color: ${color}"></span>
      </div>
    `,
    iconSize: [32, 32],
    iconAnchor: [16, 16],
  });

  return (
    <Marker position={position} icon={icon}>
      <Tooltip permanent={false}>
        <strong>{deviceName}</strong>
        <br />
        {timestamp}
      </Tooltip>
    </Marker>
  );
}

/**
 * Single device path: polyline + circle markers (with tooltip) + newest-point ripple.
 * Downsamples to MAX_DISPLAY_POINTS when there are many points to keep map responsive.
 */
function PathLayer({ deviceName, color, points }) {
  const safePoints = useMemo(() => {
    const raw = (points || []).filter(
      (p) => p != null && p.latitude != null && p.longitude != null && !Number.isNaN(Number(p.latitude)) && !Number.isNaN(Number(p.longitude))
    );
    return simplifyPath(raw, MAX_DISPLAY_POINTS);
  }, [points]);

  const positions = useMemo(
    () => safePoints.map((p) => [Number(p.latitude), Number(p.longitude)]),
    [safePoints]
  );
  const lastPoint = safePoints.length > 0 ? safePoints[safePoints.length - 1] : null;

  if (positions.length === 0) return null;

  // Last-location-only (single point): show only pulsating marker
  if (safePoints.length <= 1 && lastPoint) {
    return (
      <NewestPointMarker
        position={[Number(lastPoint.latitude), Number(lastPoint.longitude)]}
        color={color}
        deviceName={deviceName}
        timestamp={lastPoint.timestamp != null ? String(lastPoint.timestamp) : ''}
      />
    );
  }

  return (
    <>
      <Polyline
        positions={positions}
        pathOptions={{ color, weight: 4, opacity: 0.8 }}
      />
      {safePoints.map((p, i) => (
        <CircleMarker
          key={`${p.timestamp}-${i}`}
          center={[Number(p.latitude), Number(p.longitude)]}
          radius={4}
          pathOptions={{ color, fillColor: color, fillOpacity: 0.9, weight: 1 }}
        >
          <Tooltip>
            <strong>{deviceName}</strong>
            <br />
            {p.timestamp != null ? String(p.timestamp) : ''}
          </Tooltip>
        </CircleMarker>
      ))}
      {lastPoint && (
        <NewestPointMarker
          position={[Number(lastPoint.latitude), Number(lastPoint.longitude)]}
          color={color}
          deviceName={deviceName}
          timestamp={lastPoint.timestamp != null ? String(lastPoint.timestamp) : ''}
        />
      )}
    </>
  );
}

/**
 * Map showing device travel paths (polylines + points + newest-point ripple).
 * @param {Object} props
 * @param {Array<{ deviceId, deviceName, color, points }>} props.paths - One entry per device
 * @param {[number, number]} [props.center] - Initial map center
 * @param {number} [props.zoom] - Initial zoom
 */
export default function TravelMapView({ paths = [], center = [32.94, -96.82], zoom = 12, yardTileUrl = null }) {
  return (
    <div className="relative w-full h-full rounded-lg overflow-hidden border border-gray-200">
      <style>{`
        .gps-travel-newest-marker { background: none; border: none; }
        .gps-travel-ripple-wrapper { position: relative; width: 32px; height: 32px; display: flex; align-items: center; justify-content: center; }
        .gps-travel-ripple-dot { width: 12px; height: 12px; border-radius: 50%; box-shadow: 0 0 0 2px white; position: relative; z-index: 2; }
        .gps-travel-ripple-ring { position: absolute; width: 32px; height: 32px; border-radius: 50%; border: 3px solid var(--ripple-color, #2563eb); opacity: 0.6; animation: gps-travel-ripple 1.5s ease-out infinite; }
        .gps-travel-ripple-delay { animation-delay: 0.75s; }
        @keyframes gps-travel-ripple { 0% { transform: scale(0.3); opacity: 0.8; } 100% { transform: scale(1); opacity: 0; } }
      `}</style>
      <MapContainer
        center={center}
        zoom={zoom}
        scrollWheelZoom
        className="w-full h-full"
        style={{ minHeight: 400 }}
      >
        {yardTileUrl ? (
          <MapLibreGLLayer styleUrl={yardTileUrl} />
        ) : (
          <TileLayer url={OSM_TILE} attribution={OSM_ATTRIBUTION} />
        )}
        <FitBounds paths={paths} />
        {paths.map((path) => (
          <PathLayer
            key={path.deviceId}
            deviceName={path.deviceName}
            color={path.color}
            points={path.points}
          />
        ))}
      </MapContainer>
    </div>
  );
}
